import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { TalkingAvatarImageMode } from "@/pages/TalkingAvatar/types";

interface TalkingAvatarImageModePickerProps {
  value: TalkingAvatarImageMode | null;
  /** The user's uploaded photo (blob or CDN URL) — shown on the "use my
   *  photo" card so the choice is concrete, not abstract. */
  uploadPreviewUrl: string;
  onChange: (next: TalkingAvatarImageMode) => void;
}

/**
 * Step 2 — which image will do the talking? Either the uploaded photo as-is
 * (skips straight to the dialogue step) or a new AI-designed image (the
 * type → style → background → preview path).
 */
export function TalkingAvatarImageModePicker({
  value,
  uploadPreviewUrl,
  onChange,
}: TalkingAvatarImageModePickerProps) {
  const { t } = useLanguage();
  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.talkingAvatar.imageModeTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.talkingAvatar.imageModeHint}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <Card
          visual={
            <img
              src={uploadPreviewUrl}
              alt={t.talkingAvatar.imageModeSourceTitle}
              className="w-10 h-10 rounded-xl object-cover"
            />
          }
          title={t.talkingAvatar.imageModeSourceTitle}
          subtitle={t.talkingAvatar.imageModeSourceSubtitle}
          isActive={value === "source"}
          onClick={() => onChange("source")}
        />
        <Card
          visual={
            <div
              className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                value === "generate" ? "bg-primary text-white" : "bg-primary/10 text-primary",
              )}
            >
              <Sparkles className="w-5 h-5" />
            </div>
          }
          title={t.talkingAvatar.imageModeGenerateTitle}
          subtitle={t.talkingAvatar.imageModeGenerateSubtitle}
          isActive={value === "generate"}
          onClick={() => onChange("generate")}
        />
      </div>
    </div>
  );
}

function Card({
  visual,
  title,
  subtitle,
  isActive,
  onClick,
}: {
  visual: React.ReactNode;
  title: string;
  subtitle: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "group flex flex-col items-start gap-2 p-4 md:p-5 rounded-2xl border text-start transition-all duration-200",
        isActive
          ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary shadow-md ring-2 ring-primary/30"
          : "bg-card border-border hover:border-primary/40 hover:shadow-sm md:hover:-translate-y-0.5",
      )}
    >
      {visual}
      <div className="space-y-0.5">
        <p className="text-sm md:text-base font-semibold text-foreground">{title}</p>
        <p className="text-xs md:text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </button>
  );
}
